/**
 * Packaging OS — Sustainability / Compliance Risk page (Phase 25A).
 * POST /api/workflows/sustainability-risk/dry-run.
 */

import { useState, type FormEvent } from "react";
import {
  DRY_RUN_CHANNELS,
  DRY_RUN_LANGUAGES,
  runSustainabilityRiskDryRun,
  type ApiResult,
  type ClaimSeverityRow,
  type DryRunChannel,
  type DryRunLanguage,
  type SustainabilityRiskRequest,
  type SustainabilityRiskResponse,
  runWorkflowPersistedFirst,
} from "../api/client";
import { AIAssistToggle } from "../components/AIAssistToggle";
import { Button } from "../components/Button";
import { DataState, RequestIdBadge } from "../components/DataState";
import {
  FieldGrid,
  SelectField,
  TextAreaField,
  TextField,
} from "../components/FormField";
import { BulletList, ReportSection } from "../components/ReportSection";
import { RiskPill } from "../components/RiskPill";
import { strings, type UiStrings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";

interface FormState {
  product_name: string;
  category: string;
  target_market: string;
  target_language: DryRunLanguage;
  sales_channel: string;
  packaging_material: string;
  sustainability_claims: string;
  label_copy_drafts: string;
  has_recyclable_claim: boolean;
  has_biodegradable_claim: boolean;
  has_compostable_claim: boolean;
  has_origin_claim: boolean;
  barcode_present: boolean;
  expiry_present: boolean;
  origin_label_present: boolean;
  warning_present: boolean;
  notes: string;
}

const EMPTY: FormState = {
  product_name: "",
  category: "",
  target_market: "",
  target_language: "en",
  sales_channel: "",
  packaging_material: "",
  sustainability_claims: "",
  label_copy_drafts: "",
  has_recyclable_claim: false,
  has_biodegradable_claim: false,
  has_compostable_claim: false,
  has_origin_claim: false,
  barcode_present: false,
  expiry_present: false,
  origin_label_present: false,
  warning_present: false,
  notes: "",
};

type LoadState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "loaded"; result: ApiResult<SustainabilityRiskResponse> };

interface FieldErrors {
  product_name?: string;
  category?: string;
  target_market?: string;
}

function splitLines(s: string, max: number): string[] {
  return s
    .split("\n")
    .map((x) => x.trim())
    .filter((x) => x.length > 0)
    .slice(0, max);
}

function severityToRiskLevel(
  s: ClaimSeverityRow["severity"],
): "low" | "medium" | "high" {
  if (s === "high" || s === "review_required") return "high";
  if (s === "medium") return "medium";
  return "low";
}

function severityLabel(t: UiStrings, s: ClaimSeverityRow["severity"]): string {
  if (s === "high" || s === "review_required") return t.reportRiskHigh;
  if (s === "medium") return t.reportRiskMedium;
  return t.reportRiskLow;
}

export function SustainabilityRiskPage(): JSX.Element {
  const [locale] = useLocale();
  const t = strings(locale);
  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY,
    target_language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
      ? locale
      : "en") as DryRunLanguage,
  }));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [state, setState] = useState<LoadState>({ kind: "idle" });
  const [aiAssist, setAiAssist] = useState<boolean>(false);

  function update<K extends keyof FormState>(k: K, v: FormState[K]): void {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function validate(): FieldErrors {
    const e: FieldErrors = {};
    if (!form.product_name.trim()) e.product_name = t.workflowDryRunRequiredHint;
    if (!form.category.trim()) e.category = t.workflowDryRunRequiredHint;
    if (!form.target_market.trim())
      e.target_market = t.workflowDryRunRequiredHint;
    return e;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const body: SustainabilityRiskRequest = {
      product_name: form.product_name.trim(),
      category: form.category.trim(),
      target_market: form.target_market.trim().toUpperCase(),
      target_language: form.target_language,
      sales_channel: form.sales_channel
        ? (form.sales_channel as DryRunChannel)
        : null,
      packaging_material: form.packaging_material.trim() || null,
      sustainability_claims: splitLines(form.sustainability_claims, 32),
      label_copy_drafts: splitLines(form.label_copy_drafts, 32),
      has_recyclable_claim: form.has_recyclable_claim,
      has_biodegradable_claim: form.has_biodegradable_claim,
      has_compostable_claim: form.has_compostable_claim,
      has_origin_claim: form.has_origin_claim,
      barcode_present: form.barcode_present,
      expiry_present: form.expiry_present,
      origin_label_present: form.origin_label_present,
      warning_present: form.warning_present,
      notes: form.notes.trim() || null,
      ai_assist: aiAssist,
    };
    setState({ kind: "submitting" });
    const result = await runWorkflowPersistedFirst("sustainability-risk", body, runSustainabilityRiskDryRun);
    setState({ kind: "loaded", result });
  }

  function onReset(): void {
    setForm({
      ...EMPTY,
      target_language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
        ? locale
        : "en") as DryRunLanguage,
    });
    setErrors({});
    setState({ kind: "idle" });
  }

  const opt = (vals: readonly string[]) =>
    vals.map((v) => ({ value: v, label: v }));

  const yesNo = [
    { value: "no", label: t.reportReviewRequiredNo },
    { value: "yes", label: t.reportReviewRequiredYes },
  ];

  return (
    <AppLayout>
      <header className="po-section-header">
        <span className="po-section-header__eyebrow">
          {t.appShellNavWorkflows}
        </span>
        <h1 className="po-section-header__title">{t.sustTitle}</h1>
        <p className="po-note">{t.sustSubtitle}</p>
      </header>

      <section className="po-card">
        <form className="po-form" onSubmit={onSubmit} noValidate>
          <FieldGrid>
            <TextField
              label={t.intakeFieldProductName}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.product_name}
              onChange={(v) => update("product_name", v)}
              error={errors.product_name}
              maxLength={200}
            />
            <TextField
              label={t.intakeFieldProductCategory}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.category}
              onChange={(v) => update("category", v)}
              error={errors.category}
              maxLength={100}
            />
            <TextField
              label={t.labelCopyFieldTargetMarket}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.target_market}
              onChange={(v) => update("target_market", v.toUpperCase())}
              error={errors.target_market}
              maxLength={8}
            />
            <SelectField
              label={t.labelCopyFieldTargetLanguage}
              value={form.target_language}
              onChange={(v) => update("target_language", v as DryRunLanguage)}
              options={opt(DRY_RUN_LANGUAGES)}
            />
            <SelectField
              label={t.labelCopyFieldSalesChannel}
              value={form.sales_channel}
              onChange={(v) => update("sales_channel", v)}
              options={opt(DRY_RUN_CHANNELS)}
              emptyLabel={t.workflowDryRunNotSelected}
            />
            <TextField
              label={t.sustFieldPackagingMaterial}
              value={form.packaging_material}
              onChange={(v) => update("packaging_material", v)}
              maxLength={200}
            />
            <SelectField
              label={t.sustFieldRecyclable}
              value={form.has_recyclable_claim ? "yes" : "no"}
              onChange={(v) =>
                update("has_recyclable_claim", v === "yes")
              }
              options={yesNo}
            />
            <SelectField
              label={t.sustFieldBiodegradable}
              value={form.has_biodegradable_claim ? "yes" : "no"}
              onChange={(v) =>
                update("has_biodegradable_claim", v === "yes")
              }
              options={yesNo}
            />
            <SelectField
              label={t.sustFieldCompostable}
              value={form.has_compostable_claim ? "yes" : "no"}
              onChange={(v) =>
                update("has_compostable_claim", v === "yes")
              }
              options={yesNo}
            />
            <SelectField
              label={t.sustFieldOrigin}
              value={form.has_origin_claim ? "yes" : "no"}
              onChange={(v) => update("has_origin_claim", v === "yes")}
              options={yesNo}
            />
            <SelectField
              label={t.sustFieldBarcode}
              value={form.barcode_present ? "yes" : "no"}
              onChange={(v) => update("barcode_present", v === "yes")}
              options={yesNo}
            />
            <SelectField
              label={t.sustFieldExpiry}
              value={form.expiry_present ? "yes" : "no"}
              onChange={(v) => update("expiry_present", v === "yes")}
              options={yesNo}
            />
            <SelectField
              label={t.sustFieldOriginLabel}
              value={form.origin_label_present ? "yes" : "no"}
              onChange={(v) =>
                update("origin_label_present", v === "yes")
              }
              options={yesNo}
            />
            <SelectField
              label={t.sustFieldWarning}
              value={form.warning_present ? "yes" : "no"}
              onChange={(v) => update("warning_present", v === "yes")}
              options={yesNo}
            />
            <TextAreaField
              label={t.sustFieldClaims}
              hint={t.sustFieldClaimsHelp}
              value={form.sustainability_claims}
              onChange={(v) => update("sustainability_claims", v)}
              rows={4}
              full
            />
            <TextAreaField
              label={t.sustFieldLabelDrafts}
              value={form.label_copy_drafts}
              onChange={(v) => update("label_copy_drafts", v)}
              rows={3}
              full
            />
          </FieldGrid>

          {Object.keys(errors).length > 0 && (
            <p className="po-field__error">
              {t.workflowDryRunValidationFailed}
            </p>
          )}

          <AIAssistToggle
            checked={aiAssist}
            onChange={setAiAssist}
            label={t.aiAssistToggleLabel}
            hint={t.aiAssistToggleHint}
            banner={t.aiAssistDraftBanner}
            disabled={state.kind === "submitting"}
          />

          <div className="po-row">
            <Button
              variant="primary"
              type="submit"
              disabled={state.kind === "submitting"}
            >
              {state.kind === "submitting"
                ? t.sustSubmitting
                : t.sustSubmit}
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={onReset}
              disabled={state.kind === "submitting"}
            >
              {t.sustReset}
            </Button>
          </div>
        </form>
      </section>

      {state.kind === "loaded" && (
        <DataState
          state={{ kind: "loaded", result: state.result }}
          t={t}
          renderOk={(data, requestId) => (
            <SustReport data={data} requestId={requestId} t={t} />
          )}
        />
      )}
    </AppLayout>
  );
}

interface SustReportProps {
  data: SustainabilityRiskResponse;
  requestId: string | null;
  t: UiStrings;
}

function SustNextSupplierLink({
  t,
  hasEvidence,
}: {
  t: UiStrings;
  hasEvidence: boolean;
}): JSX.Element | null {
  const [locale] = useLocale();
  if (!hasEvidence) return null;
  const target = `/${locale}/app/workflows/supplier-rfq`;
  return (
    <p className="po-row">
      <a
        href={target}
        className="po-btn po-btn--ghost"
        data-testid="po-sust-next-supplier"
      >
        {t.sustNextSupplier} →
      </a>
    </p>
  );
}

function SustReport({ data, requestId, t }: SustReportProps): JSX.Element {
  const hasHigh = data.claim_severity_table.some(
    (r) => r.severity === "high" || r.severity === "review_required",
  );
  const hasMedium = data.claim_severity_table.some(
    (r) => r.severity === "medium",
  );
  return (
    <div className="po-stack" data-testid="po-sustainability-report">
      <div className="po-halt-banner po-halt-banner--medium" role="alert">
        {data.not_approved_notice}
      </div>
      {hasHigh && (
        <div className="po-halt-banner po-halt-banner--high" role="alert">
          {t.reportHaltBannerHigh}
        </div>
      )}
      {!hasHigh && hasMedium && (
        <div className="po-halt-banner po-halt-banner--medium" role="alert">
          {t.reportHaltBannerMedium}
        </div>
      )}
      <ReportSection title={t.sustReportClaimReview}>
        <BulletList items={data.sustainability_claim_review} />
      </ReportSection>
      <ReportSection title={t.sustReportGreenwashing}>
        <BulletList
          items={data.greenwashing_risk_flags}
          empty={t.reportNoFlags}
        />
      </ReportSection>
      <ReportSection title={t.sustReportSeverityTable}>
        {data.claim_severity_table.length === 0 ? (
          <p className="po-note po-note--muted">{t.reportNoFlags}</p>
        ) : (
          <div className="po-table-wrap">
            <table className="po-table">
              <thead>
                <tr>
                  <th scope="col">{t.sustColTerm}</th>
                  <th scope="col">{t.sustColSeverity}</th>
                  <th scope="col">{t.sustColCategory}</th>
                  <th scope="col">{t.sustColEvidence}</th>
                </tr>
              </thead>
              <tbody>
                {data.claim_severity_table.map((row, i) => (
                  <tr key={`${i}-${row.term}`}>
                    <td>
                      <span className="dir-ltr-island">{row.term}</span>
                    </td>
                    <td>
                      <RiskPill
                        level={severityToRiskLevel(row.severity)}
                        label={severityLabel(t, row.severity)}
                      />
                    </td>
                    <td>
                      <span className="dir-ltr-island">{row.category}</span>
                    </td>
                    <td>
                      {row.evidence_required
                        ? t.reportReviewRequiredYes
                        : t.reportReviewRequiredNo}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ReportSection>
      <ReportSection title={t.sustReportSafer}>
        <BulletList
          items={data.safer_sustainability_wording}
          empty={t.reportNoFlags}
        />
      </ReportSection>
      <ReportSection title={t.sustReportCompliance}>
        <BulletList items={data.compliance_reminder_checklist} />
      </ReportSection>
      <ReportSection title={t.sustReportCountry}>
        <BulletList items={data.country_review_notes} />
      </ReportSection>
      <ReportSection title={t.sustReportEvidence}>
        <BulletList items={data.required_evidence} empty={t.reportNoFlags} />
        <SustNextSupplierLink t={t} hasEvidence={data.required_evidence.length > 1} />
      </ReportSection>
      <ReportSection title={t.intakeReportHumanReview}>
        <BulletList
          items={data.human_review_required_notes}
          empty={t.reportNoFlags}
        />
      </ReportSection>
      <ReportSection title={t.reportTrustNotes}>
        <dl className="po-dl">
          <dt>{t.trustNotesRegulatoryGuarantee}</dt>
          <dd>
            {data.trust_notes.regulatory_guarantee
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
          <dt>{t.trustNotesFinalOutput}</dt>
          <dd>
            {data.trust_notes.final_output
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
          <dt>{t.reportClaimRiskReviewRequired}</dt>
          <dd>{t.reportReviewRequiredYes}</dd>
        </dl>
      </ReportSection>
      {requestId && <RequestIdBadge t={t} requestId={requestId} />}
    </div>
  );
}
